/**
 * Sales Entry Confirmation Modal
 * Shows a summary of manually entered sales data with options to confirm or edit
 */

import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Icon } from '../ui/Icon';
import { formatDateShort } from '../../utils/formatters';

// ============================================================================
// Types
// ============================================================================

interface SalesEntryData {
  period_start: string;
  period_end: string;
  covers_count: number;
}

interface SalesEntryConfirmationModalProps {
  isVisible: boolean;
  data: SalesEntryData;
  onConfirm: () => void;
  onEdit: () => void;
  onClose: () => void;
  isLoading?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

const ModalOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  transition: all var(--transition-fast);
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: var(--radius-lg);
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--border);
  position: relative;

  /* Mobile: Fit viewport better */
  @media (max-width: 767px) {
    width: 95vw;
    max-height: calc(100vh - 2rem);
    padding: 1.5rem;
    border-radius: var(--radius-md);
  }

  @media (max-width: 479px) {
    padding: 1.25rem;
    max-height: calc(100vh - 1.5rem);
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  /* Mobile: Smaller title */
  @media (max-width: 479px) {
    font-size: 1.25rem;
  }
`;

const ModalSubtitle = styled.p`
  color: var(--text-muted);
  margin: 0;
  font-size: 0.95rem;
`;

const SummarySection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  /* Mobile: Smaller section titles */
  @media (max-width: 479px) {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }
`;

const DataGrid = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--surface-subtle);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-light);

  /* Mobile: Stack on very small screens if needed */
  @media (max-width: 479px) {
    padding: 0.625rem;
    flex-wrap: wrap;
    gap: 0.25rem;
  }
`;

const DataLabel = styled.span`
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const DataValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.95rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);

  /* Mobile: Reduce spacing and stack buttons */
  @media (max-width: 767px) {
    margin-top: 1.5rem;
    padding-top: 1.25rem;
  }

  @media (max-width: 479px) {
    margin-top: 1.25rem;
    padding-top: 1rem;
    flex-direction: column-reverse;
    gap: 0.75rem;
  }
`;

const ActionButton = styled.button<{
  variant: 'edit' | 'confirm';
  disabled?: boolean;
}>`
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: 0.95rem;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all var(--transition-fast);
  min-width: 120px;
  min-height: 2.75rem; /* 44px touch target */
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  ${(props) =>
    props.variant === 'edit'
      ? `
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-secondary);
    
    &:hover:not(:disabled) {
      background: var(--interactive-hover);
      border-color: var(--border-strong);
      color: var(--text-primary);
    }
  `
      : `
    background: var(--success-600);
    border: 1px solid var(--success-600);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--success-500);
      border-color: var(--success-500);
    }
  `}

  /* Mobile: Full width and increased touch target */
  @media (max-width: 479px) {
    width: 100%;
    min-height: 3rem; /* 48px */
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-md);
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--interactive-hover);
    color: var(--text-primary);
  }

  /* Mobile: Larger touch target */
  @media (max-width: 767px) {
    width: 2.75rem; /* 44px */
    height: 2.75rem;
    top: 0.75rem;
    right: 0.75rem;
  }
`;

// ============================================================================
// Component
// ============================================================================

export const SalesEntryConfirmationModal: React.FC<
  SalesEntryConfirmationModalProps
> = ({ isVisible, data, onConfirm, onEdit, onClose, isLoading = false }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus management
  useEffect(() => {
    if (isVisible && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isVisible]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <ModalOverlay
      isVisible={isVisible}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <ModalCard
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-title"
        aria-describedby="confirmation-description"
      >
        <CloseButton onClick={onClose} aria-label="Cerrar" title="Cerrar">
          ×
        </CloseButton>

        <ModalHeader>
          <ModalTitle id="confirmation-title">
            <Icon name="success" size="sm" aria-hidden />
            Confirmar Entrada Manual
          </ModalTitle>
          <ModalSubtitle id="confirmation-description">
            Revisa los datos ingresados antes de guardar definitivamente
          </ModalSubtitle>
        </ModalHeader>

        {/* Service Period */}
        <SummarySection>
          <SectionTitle>
            <Icon name="calendar" size="sm" aria-hidden />
            Período del Servicio
          </SectionTitle>
          <DataGrid>
            <DataRow>
              <DataLabel>Fecha de Inicio</DataLabel>
              <DataValue>{formatDateShort(data.period_start)}</DataValue>
            </DataRow>
            <DataRow>
              <DataLabel>Fecha de Fin</DataLabel>
              <DataValue>{formatDateShort(data.period_end)}</DataValue>
            </DataRow>
          </DataGrid>
        </SummarySection>

        {/* Sales Data */}
        <SummarySection>
          <SectionTitle>
            <Icon name="dashboard" size="sm" aria-hidden />
            Volumen de Clientes
          </SectionTitle>
          <DataGrid>
            <DataRow>
              <DataLabel>Número de Cubiertos/Clientes</DataLabel>
              <DataValue>
                {data.covers_count.toLocaleString()} cubiertos
              </DataValue>
            </DataRow>
          </DataGrid>
        </SummarySection>

        {/* Action Buttons */}
        <ModalActions>
          <ActionButton variant="edit" onClick={onEdit} disabled={isLoading}>
            Editar Datos
          </ActionButton>
          <ActionButton
            variant="confirm"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Guardando...' : 'Confirmar y Guardar'}
          </ActionButton>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>
  );
};

export default SalesEntryConfirmationModal;
